import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  List,
  Paper,
  Radio,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconSparkles, IconTargetArrow } from "@tabler/icons-react";
import BrandGuard from "../_Shared/BrandGuard";
import BeaconLoader from "../_Shared/BeaconLoader";
import ObjectiveScoreboard from "./ObjectiveScoreboard";
import {
  activateObjective,
  closeObjective,
  createObjective,
  fetchActiveObjective,
  fetchObjectiveMentionContext,
  fetchObjectiveMetrics,
  proposeObjectives,
} from "./objectivesApi";

export default function ObjectivesPage() {
  return <BrandGuard>{({ brand }) => <ObjectivesContent brand={brand} />}</BrandGuard>;
}

/**
 * One objective runs at a time. This page is where it is chosen, watched and ended.
 *
 * The proposal flow is the only way in, deliberately. A blank form is honest and does not get filled
 * in — and the stronger reason is that the proposals ARE the documentation: three complete worked
 * examples carrying this brand's real numbers teach the expected shape of an objective better than
 * help text nobody reads. Which is why each card shows its full scoreboard rather than a summary.
 */
function ObjectivesContent({ brand }) {
  const canEdit = brand.canEdit !== false;

  const [objective, setObjective] = useState(null);
  const [metricLabels, setMetricLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState(null);
  const [proposing, setProposing] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [active, metrics] = await Promise.all([
        fetchActiveObjective(brand.id),
        fetchObjectiveMetrics(brand.id),
      ]);

      // 204 resolves to {} in api.js — test for real content.
      setObjective(active?.title ? active : null);
      setMetricLabels(
        Object.fromEntries((metrics ?? []).map((m) => [lowerFirst(m.metric), m.label])),
      );
    } catch (e) {
      setError(e?.message ?? "Could not load objectives.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [brand.id]);

  async function propose() {
    setProposing(true);
    setError(null);
    setProposals(null);
    setChosen(null);
    try {
      const result = await proposeObjectives(brand.id);
      setProposals(result?.proposals ?? []);
      if (result?.reason) setError(result.reason);
    } catch (e) {
      setError(e?.message ?? "Could not propose objectives.");
    } finally {
      setProposing(false);
    }
  }

  async function start() {
    if (chosen == null || !proposals?.[chosen]) return;

    const proposal = proposals[chosen];
    setStarting(true);
    setError(null);
    try {
      const draft = await createObjective(brand.id, {
        title: proposal.title,
        hypothesis: proposal.hypothesis,
        targets: proposal.targets.map((t) => ({
          metric: t.metric,
          subject: t.subject,
          targetValue: t.targetValue,
        })),
      });

      const activated = await activateObjective(brand.id, draft.id);

      // The backend refuses with a reason rather than throwing, so a refusal arrives as a body.
      if (activated?.reason) {
        setError(activated.reason);
        return;
      }

      notifications.show({ color: "green", message: "Objective started." });
      setProposals(null);
      setChosen(null);
      await load();
    } catch (e) {
      setError(e?.message ?? "Could not start the objective.");
    } finally {
      setStarting(false);
    }
  }

  async function close(outcome) {
    setStarting(true);
    try {
      await closeObjective(brand.id, objective.id, outcome, null);
      notifications.show({ color: "gray", message: `Objective marked ${outcome}.` });
      await load();
    } finally {
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <Center py="xl">
        <BeaconLoader size={40} />
      </Center>
    );
  }

  return (
    <Container fluid>
      <Stack gap="md" mt="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon size="md" variant="light" color="teal">
              <IconTargetArrow size={18} />
            </ThemeIcon>
            <Title order={3}>Objective</Title>
          </Group>

          {canEdit && !objective && (
            <Button
              leftSection={<IconSparkles size={16} />}
              loading={proposing}
              onClick={propose}
            >
              Propose objectives
            </Button>
          )}
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {objective ? (
          <ActiveObjective
            brandId={brand.id}
            objective={objective}
            metricLabels={metricLabels}
            canEdit={canEdit}
            busy={starting}
            onClose={close}
          />
        ) : proposals ? (
          <ProposalList
            proposals={proposals}
            metricLabels={metricLabels}
            chosen={chosen}
            onChoose={setChosen}
            onStart={start}
            starting={starting}
            canEdit={canEdit}
          />
        ) : (
          <EmptyState canEdit={canEdit} />
        )}
      </Stack>
    </Container>
  );
}

function EmptyState({ canEdit }) {
  return (
    <Paper withBorder p="xl" radius="md">
      <Stack gap="sm" align="flex-start">
        <Text fw={600}>No objective running</Text>
        <Text size="sm" c="dimmed" maw={640} style={{ lineHeight: 1.6 }}>
          An objective is one thing this brand is trying to do over the next couple of weeks, with
          measurable targets and a date to be judged on. It is what makes a change in the numbers mean
          something — and what lets us stay quiet when nothing has happened.
        </Text>
        {canEdit && (
          <Text size="sm" c="dimmed">
            Ask for a few proposals to see what that looks like with your own numbers.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function MentionReview({ brandId, fromUtc }) {
  const [context, setContext] = useState(null);

  useEffect(() => {
    fetchObjectiveMentionContext(brandId)
      .then((data) => setContext(data?.mentionCount != null ? data : null))
      .catch(() => setContext(null));
  }, [brandId, fromUtc]);

  if (!context) return null;

  const aspects = context.aspects ?? [];
  const gaps = context.contentGaps ?? [];
  if (aspects.length === 0 && gaps.length === 0 && !context.mentionCount) return null;

  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap="sm">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          What the web said this cycle
        </Text>
        <Text size="sm" c="dimmed">
          {context.mentionCount} classified mention
          {context.mentionCount === 1 ? "" : "s"}. The mention target is a count;
          the review is allowed to talk about the aspects and content gaps.
        </Text>
        {aspects.length > 0 && (
          <Group gap="xs">
            {aspects.map((a) => (
              <Badge key={a.aspect} variant="light" color="gray">
                {a.aspect} · {a.count}
              </Badge>
            ))}
          </Group>
        )}
        {gaps.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Content gaps
            </Text>
            <List size="sm" spacing={4}>
              {gaps.map((g) => (
                <List.Item key={g.id}>
                  {g.title}
                  {g.actionReason ? ` — ${g.actionReason}` : g.keyQuote ? ` — “${g.keyQuote}”` : ""}
                </List.Item>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function ActiveObjective({ brandId, objective, metricLabels, canEdit, busy, onClose }) {
  const met = objective.targets.filter((t) => t.state === "achieved").length;
  const daysLeft = objective.reviewDueUtc
    ? Math.ceil((new Date(objective.reviewDueUtc) - new Date()) / 86_400_000)
    : null;
  const allMet = met === objective.targets.length && objective.targets.length > 0;

  return (
    <Stack gap="md">
      <Paper withBorder p="lg" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Text fw={600} size="lg">
              {objective.title}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Badge variant="light" color={allMet ? "teal" : "gray"}>
                {met} of {objective.targets.length} met
              </Badge>
              {daysLeft != null && (
                <Badge variant="light" color={daysLeft <= 0 ? "orange" : "gray"}>
                  {daysLeft <= 0 ? "Review due" : `${daysLeft} days left`}
                </Badge>
              )}
            </Group>
          </Group>

          {objective.hypothesis && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Hypothesis
              </Text>
              <Text size="sm" style={{ lineHeight: 1.6 }}>
                {objective.hypothesis}
              </Text>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="lg" radius="md">
        <ObjectiveScoreboard targets={objective.targets} metricLabels={metricLabels} />
      </Paper>

      <MentionReview brandId={brandId} fromUtc={objective.startedUtc} />

      {canEdit && (
        <Group gap="xs">
          {/* Three outcomes, not two. "Missed" is a legitimate result and recording it is the only
              way the hypothesis ever gets judged; deleting the objective instead would erase the
              evidence that it did not work. */}
          <Button size="xs" variant="light" color="teal" loading={busy} onClick={() => onClose("achieved")}>
            Mark achieved
          </Button>
          <Button size="xs" variant="light" color="orange" loading={busy} onClick={() => onClose("missed")}>
            Mark missed
          </Button>
          <Button size="xs" variant="subtle" color="gray" loading={busy} onClick={() => onClose("abandoned")}>
            Abandon
          </Button>
        </Group>
      )}
    </Stack>
  );
}

function ProposalList({ proposals, metricLabels, chosen, onChoose, onStart, starting, canEdit }) {
  if (proposals.length === 0) {
    return (
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" c="dimmed">
          Nothing measurable to aim at yet.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Pick one. Each is complete and measured against where this brand stands now — one objective runs
        at a time, on purpose.
      </Text>

      <Radio.Group value={chosen == null ? null : String(chosen)} onChange={(v) => onChoose(Number(v))}>
        <Stack gap="md">
          {proposals.map((proposal, index) => (
            <Card key={index} withBorder padding="lg" radius="md">
              <Stack gap="sm">
                <Radio
                  value={String(index)}
                  label={
                    <Text fw={600} size="sm">
                      {proposal.title}
                    </Text>
                  }
                />

                {proposal.hypothesis && (
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                    {proposal.hypothesis}
                  </Text>
                )}

                <ObjectiveScoreboard targets={proposal.targets} metricLabels={metricLabels} />

                {proposal.notes?.length > 0 && (
                  <Stack gap={2}>
                    {/* What we refused to offer, and why. A dropped target is worth showing: it is
                        how someone learns which parts of their setup cannot be measured yet. */}
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Left out
                    </Text>
                    <List size="xs" c="dimmed" spacing={2}>
                      {proposal.notes.map((note, i) => (
                        <List.Item key={i}>{note}</List.Item>
                      ))}
                    </List>
                  </Stack>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      </Radio.Group>

      {canEdit && (
        <Group>
          <Button disabled={chosen == null} loading={starting} onClick={onStart}>
            Start this objective
          </Button>
          <Text size="xs" c="dimmed">
            Starting freezes today&apos;s numbers as the baseline.
          </Text>
        </Group>
      )}
    </Stack>
  );
}

function lowerFirst(value) {
  return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
}
